import { ApolloError } from '@apollo/client';
import { Box, ButtonGroup, styled } from '@mui/material';
import { useGeoDataCollection } from 'components/DueDiligenceProcess/Context/SubSections/GeoDataCollectionContext';
import DueDiligenceProcessSubSectionNavigation from 'components/DueDiligenceProcess/DueDiligenceProcessSubSectionNavigation';
import { useMessages } from 'components/hooks';
import { FlexBox } from 'components/Structure';
import { ThemeButton, ThemeTypography } from 'designSystem';
import useDueDiligenceProcessMutations from 'hooks/useDueDiligenceProcessMutations';
import isEqual from 'lodash/isEqual';
import sortBy from 'lodash/sortBy';
import React, { FC, useEffect, useState } from 'react';
import DatasetSelection from '../DatasetSelection';
import SiteSelection from '../SiteSelection';

enum ESelectionView {
  SITES = 'SITES',
  DATASETS = 'DATASETS',
}

const HintContainer = styled('div')(({ theme }) => ({
  background: theme.custom.themeColors.primary[5],
  borderRadius: theme.spacing(1),
  padding: theme.spacing(2),
  height: 'fit-content',
}));

const GeoDataCollectionSelection: FC = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [viewOption, setViewOption] = useState<ESelectionView>(ESelectionView.SITES);
  const { setErrorMessage } = useMessages();

  const { geoDataProcess, geoDataSelection, setGeoDataSelection, onNextClick, onPreviousClick } =
    useGeoDataCollection();
  const { createDueDiligenceProcess, updateDueDiligenceProcess } =
    useDueDiligenceProcessMutations();

  const selectedDatasetIds = (geoDataSelection.datasetIdsSelection ?? []).map(({ id }) => id);
  const selectedSiteIds = geoDataSelection.siteIds || [];
  const selectedClusterIds = (geoDataSelection.clusterIdsSelection ?? []).map(({ id }) => id);

  const handleNextStepClick = async () => {
    try {
      setLoading(true);

      // Create new due diligence process if it does not exist
      if (!geoDataProcess) {
        const createdDueDiligenceProcess = await createDueDiligenceProcess({
          complianceId: '00000000-0000-0000-0000-000000000000',
          datasetIds: selectedDatasetIds,
          clusterIds: selectedClusterIds,
          siteIds: selectedSiteIds,
        });
        const dueDiligenceProcessId =
          createdDueDiligenceProcess.data?.createGeoDataProcess.geoDataProcess.id;
        if (dueDiligenceProcessId) {
          onNextClick(dueDiligenceProcessId);
        } else {
          throw new Error('Failed to create due diligence process');
        }
        // Update existing due diligence process with selected geo data if they changed
      } else if (
        !isEqual(
          sortBy(geoDataProcess.geoDatas.map(({ datasetId }) => datasetId)),
          sortBy(selectedDatasetIds)
        ) ||
        !isEqual(
          sortBy(geoDataProcess.geoDatas.map(({ siteClusterId }) => siteClusterId)),
          sortBy(selectedClusterIds)
        ) ||
        !isEqual(
          sortBy(
            geoDataProcess.geoDatas.map(({ sites }) => sites?.edges.map(({ node }) => node.id))
          ),
          sortBy(selectedSiteIds)
        )
      ) {
        await updateDueDiligenceProcess(geoDataProcess.id, {
          datasetIdsSelection: geoDataSelection.datasetIdsSelection,
          clusterIdsSelection: geoDataSelection.clusterIdsSelection,
          siteIds: geoDataSelection.siteIds,
        });
        onNextClick();
        // Do nothing just continue to the next step
      } else {
        onNextClick();
      }
    } catch (error) {
      if (
        error instanceof ApolloError &&
        error.graphQLErrors.length &&
        error.graphQLErrors.findIndex(
          ({ message }) => message === 'GEO_DATA_ERROR_MISSING_ORIGIN_COUNTRY'
        ) !== -1
      ) {
        setErrorMessage(
          'The origin country is missing for one or more selected clusters, datasets or sites. Please make sure to add the correct locations to those before proceeding.'
        );
      } else {
        // eslint-disable-next-line no-console
        console.error(error);
        setErrorMessage('Failed to create due diligence process');
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * By default the sites view option is selected. But if no cluster / sites are selected, but datasets are selected, then the datasets view option is being selected by default.
   */
  useEffect(() => {
    if (
      viewOption === ESelectionView.SITES &&
      !selectedSiteIds.length &&
      !selectedClusterIds.length &&
      selectedDatasetIds.length > 0
    ) {
      setViewOption(ESelectionView.DATASETS);
    }
    // Run once initial render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <DueDiligenceProcessSubSectionNavigation
      allowNextStepNavigation={
        !!selectedDatasetIds.length || !!selectedClusterIds.length || !!selectedSiteIds.length
      }
      nextStepLoading={loading}
      onPreviousStepClick={onPreviousClick}
      onNextStepClick={handleNextStepClick}
    >
      <HintContainer>
        <ThemeTypography variant="BODY_MEDIUM" color="BLUE_80">
          Only datasets that have EUDR relevant raw materials are shown here. Those are materials
          for the following commodities: Cattle, cocoa, coffee, oil palm, rubber, soya and wood.
        </ThemeTypography>
      </HintContainer>

      <Box pt={2}>
        <FlexBox gap={1} mb={0.5}>
          <ButtonGroup>
            <ThemeButton
              className={viewOption === ESelectionView.SITES ? 'selected' : ''}
              onClick={() => setViewOption(ESelectionView.SITES)}
            >
              Sites
            </ThemeButton>
            <ThemeButton
              className={viewOption === ESelectionView.DATASETS ? 'selected' : ''}
              onClick={() => setViewOption(ESelectionView.DATASETS)}
            >
              Datasets
            </ThemeButton>
          </ButtonGroup>
          {(selectedDatasetIds.length > 0 ||
            selectedClusterIds.length > 0 ||
            selectedSiteIds.length > 0) && (
            <Box>
              <ThemeTypography variant="BODY_MEDIUM">
                <b>(Selected: </b>
                {selectedClusterIds.length} origin clusters, {selectedSiteIds.length} individual
                sites, {selectedDatasetIds.length} datasets
                <b>)</b>
              </ThemeTypography>
            </Box>
          )}
        </FlexBox>

        {viewOption === ESelectionView.SITES && (
          <SiteSelection
            selectedSiteIds={selectedSiteIds}
            selectedClusterIds={selectedClusterIds}
            setSelectedSiteIds={(siteIds: string[]) =>
              setGeoDataSelection(prev => ({
                ...prev,
                siteIds,
              }))
            }
            setSelectedClusterIds={(clusterIds: string[]) =>
              setGeoDataSelection(prev => ({
                ...prev,
                clusterIdsSelection: clusterIds.map(id => ({ id, excludedSitesIds: [] })),
              }))
            }
          />
        )}

        {viewOption === ESelectionView.DATASETS && (
          <DatasetSelection
            selectedDatasetIds={selectedDatasetIds}
            setSelectedDatasetIds={(datasetIds: string[]) =>
              setGeoDataSelection(prev => ({
                ...prev,
                datasetIdsSelection: datasetIds.map(id => ({ id, excludedSitesIds: [] })),
              }))
            }
          />
        )}
      </Box>
    </DueDiligenceProcessSubSectionNavigation>
  );
};

export default GeoDataCollectionSelection;
